/**
 * AuthorByline — reusable author strip used on BlogCards + article headers.
 *
 * Renders: avatar (24 or 32 px) + name (+ optional role) + optional date.
 * - If an avatar image is missing/404s, falls back to initials on a brand-tinted circle.
 * - When authorSlug + site are provided, name links to /:site/author/:slug.
 */
import { User, Clock } from 'lucide-react';
import { formatDate } from '@/lib/datetime';
import Link from '@/lib/siteLink';
import { resolveAuthor, authorHref } from '@/lib/authors';

export default function AuthorByline({
  articleAuthor,
  site,
  date,
  size = 'sm',           // 'sm' (24px avatar) | 'md' (32px) | 'lg' (48px)
  showRole = false,
  accent = '#111827',
  className = '',
  asLink = true,          // set false when already nested inside another <a> (e.g. BlogCard)
}) {
  const author = resolveAuthor(articleAuthor, site);
  const displayName = author?.name || articleAuthor || 'Editorial';
  const initials = displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const avatarPx = size === 'lg' ? 48 : size === 'md' ? 32 : 24;
  const nameCls = size === 'lg' ? 'text-sm font-semibold' : 'text-xs font-medium';
  const linkTo = asLink && author ? authorHref(author, site) : '';

  const AvatarInner = () => (
    <div
      className="rounded-full flex items-center justify-center text-white font-serif font-bold flex-shrink-0 overflow-hidden"
      style={{ width: avatarPx, height: avatarPx, backgroundColor: accent, fontSize: avatarPx * 0.38 }}
      aria-hidden="true"
    >
      {author?.image ? (
        <img
          src={author.image}
          alt=""
          width={avatarPx}
          height={avatarPx}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fall back to initials by hiding the img; parent circle already renders initials as a sibling.
            e.currentTarget.style.display = 'none';
            if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}
      <span
        className="w-full h-full flex items-center justify-center"
        style={{ display: author?.image ? 'none' : 'flex' }}
      >
        {initials || <User className="w-3 h-3" />}
      </span>
    </div>
  );

  const NameEl = () => (
    <span
      className={`${nameCls} text-gray-700 truncate max-w-[160px]`}
      title={displayName}
    >
      {displayName}
    </span>
  );

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="author-byline">
      {linkTo ? (
        <Link to={linkTo} className="flex items-center gap-2 group" data-testid={`author-link-${author.slug}`}>
          <AvatarInner />
          <span className="min-w-0 flex flex-col leading-tight">
            <span className={`${nameCls} text-gray-700 group-hover:text-black transition-colors truncate max-w-[160px]`} title={displayName}>{displayName}</span>
            {showRole && author?.role && (
              <span className="text-[10px] text-gray-400 truncate max-w-[160px]">{author.role}</span>
            )}
          </span>
        </Link>
      ) : (
        <>
          <AvatarInner />
          <NameEl />
        </>
      )}
      {date && (
        <>
          <span className="text-gray-300 text-xs">·</span>
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <Clock className="w-3 h-3" />{formatDate(date)}
          </span>
        </>
      )}
    </div>
  );
}
